import { UserGroupIcon } from "@heroicons/react/24/outline";
import { ChevronRightIcon } from "@radix-ui/react-icons";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { useGetInstance } from "src/api/General/instance";
import { useGetSettings, useUpdateSettings } from "src/api/General/settings";
import { Badge } from "src/components/ui/badge";
import { Button } from "src/components/ui/button";
import { Loading } from "src/components/ui/loading";
import { useRealmApp } from "src/store/RealmApp";

type Props = {};

export default function ResourceManagementSettings({}: Props) {
  const { instance } = useGetInstance();
  const { data: settings } = useGetSettings();
  return (
    <div className="flex h-full flex-col">
      <div className="flex h-12 items-center border-b border-gray-200/70 px-8 text-sm">
        <div className="flex items-center space-x-2 text-sm">
          <Link to={`/settings`}>
            <div className="text-gray-800">{instance?.name}</div>
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">{"Resources"}</div>
        </div>
      </div>
      <div className="flex-1 overflow-auto">
        <div className="mx-auto mt-20 max-w-3xl px-8">
          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-md border shadow-sm">
                <UserGroupIcon className="h-4 w-4 text-gray-600" />
                {/* <svg
              width="32px"
              height="32px"
              viewBox="0 0 16 16"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
            >
              <g fill-rule="evenodd" clip-rule="evenodd">
                <path
                  fill="#E01E5A"
                  d="M2.471 11.318a1.474 1.474 0 001.47-1.471v-1.47h-1.47A1.474 1.474 0 001 9.846c.001.811.659 1.469 1.47 1.47zm3.682-2.942a1.474 1.474 0 00-1.47 1.471v3.683c.002.811.66 1.468 1.47 1.47a1.474 1.474 0 001.47-1.47V9.846a1.474 1.474 0 00-1.47-1.47z"
                />
                <path
                  fill="#36C5F0"
                  d="M4.683 2.471c.001.811.659 1.469 1.47 1.47h1.47v-1.47A1.474 1.474 0 006.154 1a1.474 1.474 0 00-1.47 1.47zm2.94 3.682a1.474 1.474 0 00-1.47-1.47H2.47A1.474 1.474 0 001 6.153c.002.812.66 1.469 1.47 1.47h3.684a1.474 1.474 0 001.47-1.47z"
                />
                <path
                  fill="#2EB67D"
                  d="M9.847 7.624a1.474 1.474 0 001.47-1.47V2.47A1.474 1.474 0 009.848 1a1.474 1.474 0 00-1.47 1.47v3.684c.002.81.659 1.468 1.47 1.47zm3.682-2.941a1.474 1.474 0 00-1.47 1.47v1.47h1.47A1.474 1.474 0 0015 6.154a1.474 1.474 0 00-1.47-1.47z"
                />
                <path
                  fill="#ECB22E"
                  d="M8.377 9.847c.002.811.659 1.469 1.47 1.47h3.683A1.474 1.474 0 0015 9.848a1.474 1.474 0 00-1.47-1.47H9.847a1.474 1.474 0 00-1.47 1.47zm2.94 3.682a1.474 1.474 0 00-1.47-1.47h-1.47v1.47c.002.812.659 1.469 1.47 1.47a1.474 1.474 0 001.47-1.47z"
                />
              </g>
            </svg> */}
              </div>
              <div>
                <h2 className="cursor-default text-lg font-medium tracking-wide text-gray-900">
                  Resources
                </h2>
                {/* <p className="text-sm font-light text-gray-500">
              Sync calendar events to enable automations in our AI Note Taker,
              Timesheets and more.
            </p> */}
              </div>
            </div>

            <IntegrationStatus />
          </div>
          <div className="mt-8">
            <ResourceManagement />
          </div>
        </div>
      </div>
    </div>
  );
}

function IntegrationStatus() {
  const { data: settings } = useGetSettings();
  if (!settings) {
    return <div className="h-7 w-16 animate-pulse rounded-md bg-gray-100" />;
  }
  if (!settings.apps?.resourceManagement.isAccessible) {
    return (
      <Badge variant={"gray"} className="h-7 px-2.5">
        Disabled
      </Badge>
    );
  }
  if (settings.apps?.resourceManagement.isEnabled) {
    return (
      <Badge variant={"green"} className="h-7 px-2.5">
        Active
      </Badge>
    );
  }
  return (
    <Badge variant={"gray"} className="h-7 px-2.5">
      Inactive
    </Badge>
  );
}

function ResourceManagement() {
  const { data: settings } = useGetSettings();
  // console.log(settings);
  const app = useRealmApp();
  const queryClient = useQueryClient();
  const updateSettingsMutation = useUpdateSettings();
  return (
    <>
      {!!settings ? (
        <>
          {settings?.apps.resourceManagement.isAccessible ? (
            <div className="space-y-4">
              <div className="divide-y rounded-md border p-8 shadow-sm">
                {!!settings?.apps?.resourceManagement.isEnabled ? (
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="leading-none text-gray-900">Overview</div>
                      <div className="text-sm font-light leading-relaxed text-gray-600">
                        Our resource management app helps you manage resources
                        so you can staff projects efficiently and analyze the
                        capacity of your team.
                      </div>
                    </div>
                    <div>
                      <Button
                        variant={"secondary"}
                        onClick={() => {
                          updateSettingsMutation.mutate({
                            update: {
                              apps: {
                                ...settings?.apps,
                                resourceManagement: {
                                  ...settings?.apps?.resourceManagement,
                                  isEnabled: false,
                                },
                              },
                            },
                          });
                        }}
                      >
                        Disable
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    <div className="space-y-4">
                      <div className="leading-none text-gray-900">Overview</div>
                      <div className="text-sm font-light leading-relaxed text-gray-600">
                        Our resource management app helps you manage resources
                        so you can staff projects efficiently and analyze the
                        capacity of your team.
                      </div>
                    </div>
                    <div>
                      <Button
                        onClick={async () => {
                          updateSettingsMutation.mutate({
                            update: {
                              apps: {
                                ...settings?.apps,
                                resourceManagement: {
                                  ...settings?.apps?.resourceManagement,
                                  isEnabled: true,
                                },
                              },
                            },
                          });
                        }}
                      >
                        Enable
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="divide-y rounded-md border p-8 shadow-sm">
                <div className="space-y-6">
                  <div className="space-y-4">
                    <div className="leading-none text-gray-900">Overview</div>
                    <div className="text-sm font-light leading-relaxed text-gray-600">
                      Our resource management app helps you manage resources so
                      you can staff projects efficiently and analyze the
                      capacity of your team.
                    </div>
                  </div>
                  <div className="text-sm font-light leading-relaxed text-gray-800 underline">
                    Resources is currently disabled. Please reach out to us if
                    you want to enable it.
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <Loading className="mx-auto mt-[10vh]" />
      )}
    </>
  );
}
